import useAuth from "../Users/hooks/useAuth";
import { useCallback, useEffect, useMemo, useState } from "react";
import PresentationTitle from "./Components/PresentationTitle";

const LANGUAGE_OPTIONS = [
    "Norwegian",
    "English",
    "Russian",
    "Swedish",
    "French",
    "Spanish",
    "Portuguese",
    "German",
    "Italian",
    "Polish",
    "Vietnamese",
];

const SORT_OPTIONS = [
    { value: "recent", label: "Recently updated" },
    { value: "title", label: "Title A–Z" },
    { value: "language", label: "Language" },
];

const normalize = (value) => String(value || "").trim().toLowerCase();

const getTextDate = (item) => {
    const rawDate =
        item.updated_at ||
        item.updatedAt ||
        item.modified_at ||
        item.modifiedAt ||
        item.created_at ||
        item.createdAt ||
        item.date ||
        "";

    if (!rawDate) return null;

    const date = new Date(rawDate);
    return Number.isNaN(date.getTime()) ? null : date;
};

const isSameCalendarDay = (a, b) => {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
};

const formatTextDate = (item) => {
    const date = getTextDate(item);
    if (!date) return "—";

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (isSameCalendarDay(date, today)) return "Today";
    if (isSameCalendarDay(date, yesterday)) return "Yesterday";

    return new Intl.DateTimeFormat(undefined, {
        day: "numeric",
        month: "short",
        year: date.getFullYear() === today.getFullYear() ? undefined : "numeric",
    }).format(date);
};

const getTextStatus = (item) => {
    const status = normalize(item.text_status || item.status);

    if (status === "ready") {
        const count = Number(item.segment_count || item.segmentCount || 0);
        return {
            label: count > 0 ? `Ready · ${count}` : "Ready",
            className: "bg-blue-50 text-blue-700 ring-blue-200",
        };
    }

    if (status === "empty") {
        return {
            label: "Empty",
            className: "bg-slate-100 text-slate-600 ring-slate-200",
        };
    }

    if (item.latest_version || item.latestVersion) {
        return {
            label: "Ready",
            className: "bg-blue-50 text-blue-700 ring-blue-200",
        };
    }

    return {
        label: "—",
        className: "bg-slate-50 text-slate-400 ring-slate-200",
    };
};

const getPresentationKey = (item) => `${normalize(item?.title)}::${normalize(item?.language)}`;

const getSharedRecordingTitle = (recording) => (
    recording?.title ||
    recording?.presentation_title ||
    recording?.metadata?.title ||
    ""
);

const getSharedRecordingLanguage = (recording) => (
    recording?.language ||
    recording?.metadata?.language ||
    ""
);

const getSharedRecordingStatus = (recording) => normalize(recording?.status || recording?.visibility || "active");

const isActiveSharedRecording = (recording) => {
    const status = getSharedRecordingStatus(recording);
    return !["unshared", "deleted", "inactive", "revoked"].includes(status);
};

const sharedRecordingKey = (recording) => getPresentationKey({
    title: getSharedRecordingTitle(recording),
    language: getSharedRecordingLanguage(recording),
});

const groupSharedRecordingsByText = (recordings = []) => {
    return recordings
        .filter((recording) => recording?.recording_id && isActiveSharedRecording(recording))
        .reduce((acc, recording) => {
            const key = sharedRecordingKey(recording);
            if (!key || key === "::") return acc;

            if (!acc[key]) acc[key] = [];
            acc[key].push(recording);

            return acc;
        }, {});
};

const DeleteTextDialog = ({ text, onCancel, onConfirm, isDeleting }) => {
    if (!text) return null;

    const statusInfo = getTextStatus(text);

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/40 px-4 py-6 backdrop-blur-md">
            <div className="w-full max-w-md rounded-[28px] bg-white p-6 shadow-[0_30px_100px_rgba(15,23,42,0.28)] ring-1 ring-slate-200">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    </svg>
                </div>

                <h2 className="text-xl font-bold tracking-tight text-slate-950">Delete this narration text?</h2>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                    This will delete the narration text and all transcript versions connected to it.
                </p>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="truncate text-sm font-semibold text-slate-950">{text.title}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                        <span>{text.language}</span>
                        <span aria-hidden="true">·</span>
                        <span>{formatTextDate(text)}</span>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${statusInfo.className}`}>
                            {statusInfo.label}
                        </span>
                    </div>
                </div>

                <p className="mt-4 text-sm font-medium text-red-600">This cannot be undone.</p>

                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isDeleting}
                        className="inline-flex h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={isDeleting}
                        className="inline-flex h-11 items-center justify-center rounded-2xl bg-red-600 px-5 text-sm font-semibold text-white shadow-lg shadow-red-600/20 transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
                    >
                        {isDeleting ? "Deleting..." : "Delete text"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const YourPresentations = ({ successHandler }) => {
    const { setAuth } = useAuth();
    const [language, setLanguage] = useState("");
    const [presentations, setPresentations] = useState([]);
    const [title, setTitle] = useState("");
    const [currentPresentation, setPresentation] = useState();
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingKey, setDeletingKey] = useState("");
    const [textToDelete, setTextToDelete] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortMode, setSortMode] = useState("recent");
    const [sharedRecordingsByText, setSharedRecordingsByText] = useState({});
    const [isLoadingSharedRecordings, setIsLoadingSharedRecordings] = useState(false);
    const [unsharingKey, setUnsharingKey] = useState("");
    const [sharedStatusMessage, setSharedStatusMessage] = useState("");

    const trimmedTitle = title.trim();
    const canCreate = trimmedTitle.length > 0 && language.length > 0 && !isSaving;

    const sharedTextCount = useMemo(() => Object.keys(sharedRecordingsByText).length, [sharedRecordingsByText]);

    const visibleTexts = useMemo(() => {
        const query = normalize(searchQuery);

        return [...presentations]
            .filter((item) => {
                if (!query) return true;
                return `${item.title || ""} ${item.language || ""}`.toLowerCase().includes(query);
            })
            .sort((a, b) => {
                if (sortMode === "title") {
                    return (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" });
                }

                if (sortMode === "language") {
                    const languageCompare = (a.language || "").localeCompare(b.language || "", undefined, { sensitivity: "base" });
                    if (languageCompare !== 0) return languageCompare;
                    return (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" });
                }

                const dateA = getTextDate(a)?.getTime() || 0;
                const dateB = getTextDate(b)?.getTime() || 0;
                if (dateB !== dateA) return dateB - dateA;

                return (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" });
            });
    }, [presentations, searchQuery, sortMode]);

    const sendData = async (outdata, route) => {
        const token = localStorage.getItem("token");
        const url = `${process.env.REACT_APP_USER_API_URL}/presentation/${route}`;

        try {
            const response = await fetch(url, {
                method: "POST",
                body: JSON.stringify(outdata),
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
                return null;
            }

            if (!response.ok) {
                throw new Error(`Request failed with status: ${response.status}`);
            }

            return await response.json();
        } catch (requestError) {
            console.error("Narration text request failed:", requestError);
            throw requestError;
        }
    };

    const fetchData = async (route, method) => {
        const token = localStorage.getItem("token");
        const url = `${process.env.REACT_APP_USER_API_URL}/presentation/${route}`;

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
                return null;
            }

            if (!response.ok) {
                throw new Error(`Fetch failed with status: ${response.status}`);
            }

            return await response.json();
        } catch (requestError) {
            console.error("Narration text fetch failed:", requestError);
            throw requestError;
        }
    };

    const getSharedRecordings = useCallback(async () => {
        setIsLoadingSharedRecordings(true);

        try {
            const token = localStorage.getItem("token");
            const params = new URLSearchParams({
                language: "all",
                source: "all",
                status: "active",
                sort: "newest",
                includeItems: "none",
            });

            const response = await fetch(
                `${process.env.REACT_APP_USER_API_URL}/presentation/shared-recordings?${params.toString()}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                },
            );

            if (!response.ok) {
                throw new Error(`Shared recordings failed with status: ${response.status}`);
            }

            const payload = await response.json();
            const recordings = payload.items || payload.recordings || [];

            setSharedRecordingsByText(groupSharedRecordingsByText(recordings));
        } catch (requestError) {
            console.error("Failed to fetch shared narration texts:", requestError);
            setSharedRecordingsByText({});
        } finally {
            setIsLoadingSharedRecordings(false);
        }
    }, []);

    const getPresentation = useCallback(async () => {
        setIsLoading(true);
        try {
            const json = await fetchData("get-presentations", "GET");
            if (json) {
                setPresentations(json);
            }

            await getSharedRecordings();
        } catch (requestError) {
            console.error("Failed to fetch narration texts:", requestError);
            setError("We could not load your texts. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [getSharedRecordings]);

    const createPresentation = async (event) => {
        event.preventDefault();
        setError("");

        if (!trimmedTitle) {
            setError("Enter a title first.");
            return;
        }

        if (!language) {
            setError("Select the language of the text.");
            return;
        }

        const alreadyExists = presentations.some(
            (item) => normalize(item.title) === normalize(trimmedTitle) && item.language === language,
        );

        if (alreadyExists) {
            setError("A text with this title and language already exists.");
            return;
        }

        setIsSaving(true);
        try {
            await sendData({ title: trimmedTitle, language }, "create-presentation");
            await getPresentation();
            setTitle("");
            setLanguage("");
            setPresentation({ title: trimmedTitle, language });
        } catch (requestError) {
            console.error("Failed to create narration text:", requestError);
            setError("We could not create this text. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const requestDeletePresentation = (presentation) => {
        setTextToDelete(presentation);
        setError("");
    };

    const cancelDeletePresentation = () => {
        if (deletingKey) return;
        setTextToDelete(null);
    };

    const confirmDeletePresentation = async () => {
        if (!textToDelete || deletingKey) return;

        const key = `${textToDelete.title}-${textToDelete.language}`;
        setDeletingKey(key);
        setError("");

        try {
            await sendData({ title: textToDelete.title, language: textToDelete.language }, "delete-presentation");
            setTextToDelete(null);
            await getPresentation();
        } catch (requestError) {
            console.error("Failed to delete narration text:", requestError);
            setError("We could not delete this text. Please try again.");
        } finally {
            setDeletingKey("");
        }
    };

    const unsharePresentationRecordings = async (presentation) => {
        const key = getPresentationKey(presentation);
        const recordings = sharedRecordingsByText[key] || [];

        if (!recordings.length || unsharingKey) return;

        const confirmed = window.confirm(
            recordings.length === 1
                ? "Unshare this audio material? It will disappear from Community, but the original narration will stay."
                : `Unshare ${recordings.length} shared audio materials for this narration text? They will disappear from Community, but the original narration will stay.`,
        );

        if (!confirmed) return;

        const token = localStorage.getItem("token");

        setUnsharingKey(key);
        setError("");
        setSharedStatusMessage("");

        try {
            await Promise.all(
                recordings.map(async (recording) => {
                    const response = await fetch(
                        `${process.env.REACT_APP_USER_API_URL}/presentation/shared-recording/${encodeURIComponent(recording.recording_id)}`,
                        {
                            method: "DELETE",
                            headers: {
                                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                            },
                        },
                    );

                    if (!response.ok) {
                        throw new Error(`Unshare failed with status: ${response.status}`);
                    }
                }),
            );

            setSharedRecordingsByText((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });

            setSharedStatusMessage(
                recordings.length === 1
                    ? "Audio material was unshared."
                    : `${recordings.length} audio materials were unshared.`,
            );

            await getSharedRecordings();
        } catch (requestError) {
            console.error("Failed to unshare audio material:", requestError);
            setError("We could not unshare this audio material. Please try again.");
        } finally {
            setUnsharingKey("");
        }
    };

    useEffect(() => {
        getPresentation();
    }, [getPresentation]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/25 px-4 py-6 backdrop-blur-lg">
            <div className="flex h-[calc(100vh-3rem)] w-full max-w-6xl flex-col overflow-hidden rounded-[34px] border border-blue-200/70 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 shadow-[0_34px_120px_rgba(15,23,42,0.24)] ring-1 ring-white/80">
                {currentPresentation ? (
                    <div className="min-h-0 flex-1 overflow-hidden">
                        <PresentationTitle
                            presentation_title={currentPresentation}
                            successHandler={(state) => {
                                setPresentation(state);

                                if (!state) {
                                    getPresentation();
                                }
                            }}
                        />
                    </div>
                ) : (
                    <>
                        <header className="shrink-0 border-b border-blue-100/80 bg-white/95 px-7 py-5 shadow-sm backdrop-blur-xl">
                            <div className="flex flex-col gap-4">
                                <div className="flex items-start justify-between gap-6">
                                    <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start">
                                        <button
                                            type="button"
                                            onClick={() => successHandler(false)}
                                            className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-blue-100"
                                            aria-label="Back to library"
                                        >
                                            <span aria-hidden="true">←</span>
                                            <span>Back to library</span>
                                        </button>

                                        <div className="min-w-0">
                                            <p className="mb-1 text-xs font-black uppercase tracking-[0.22em] text-blue-600">Narration library</p>
                                            <h1 className="text-3xl font-black tracking-tight text-slate-950">Your narration texts</h1>
                                            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                                                Create narration texts, prepare transcripts, and record/narrate them aloud.
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        className="rounded-full p-2.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        onClick={() => successHandler(false)}
                                        aria-label="Close narration library"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="flex flex-wrap items-center gap-2 pl-0 sm:pl-[10.75rem]">
                                    <span className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">
                                        {presentations.length} saved text{presentations.length === 1 ? "" : "s"}
                                    </span>

                                    {sharedTextCount > 0 && (
                                        <span className="rounded-full border border-cyan-100 bg-cyan-50 px-3 py-1 text-xs font-black text-cyan-700">
                                            {sharedTextCount} shared
                                        </span>
                                    )}

                                    {isLoadingSharedRecordings && (
                                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-black text-slate-400">
                                            Checking shared audio…
                                        </span>
                                    )}
                                </div>
                            </div>
                        </header>

                        <main className="min-h-0 flex-1 overflow-y-auto px-7 py-6">
                            <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 pb-8">
                                <section className="relative overflow-hidden rounded-[34px] border border-emerald-100 bg-white p-5 shadow-[0_24px_70px_-46px_rgba(16,185,129,0.72)] ring-1 ring-emerald-50">
                                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-cyan-400 to-blue-500" />
                                    <div className="mb-5 rounded-[26px] border border-emerald-100 bg-gradient-to-br from-white via-emerald-50/70 to-cyan-50/40 px-5 py-4">
                                        <div className="mb-2 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-emerald-700 ring-1 ring-emerald-200">New recording source</div>
                                        <h2 className="text-xl font-bold tracking-tight text-slate-950">Create narration text</h2>
                                        <p className="mt-1 text-sm leading-6 text-slate-500">
                                            Choose a title and language. You’ll add the source text on the next screen.
                                        </p>
                                    </div>

                                    <form className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_230px_auto] lg:items-end" onSubmit={createPresentation}>
                                        <div>
                                            <label htmlFor="narration-text-title" className="block text-sm font-medium text-slate-700">
                                                Text title
                                            </label>
                                            <input
                                                id="narration-text-title"
                                                className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-slate-950 shadow-sm transition placeholder:text-slate-400 hover:border-emerald-300 focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100"
                                                type="text"
                                                placeholder="Example: Jura wine description"
                                                value={title}
                                                onChange={(event) => setTitle(event.target.value)}
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="narration-text-language" className="block text-sm font-medium text-slate-700">
                                                Language
                                            </label>
                                            <select
                                                id="narration-text-language"
                                                className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-slate-950 shadow-sm transition hover:border-slate-300 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
                                                value={language}
                                                onChange={(event) => setLanguage(event.target.value)}
                                            >
                                                <option value="">Select language</option>
                                                {LANGUAGE_OPTIONS.map((option) => (
                                                    <option key={option} value={option}>
                                                        {option}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <button
                                            type="submit"
                                            className="inline-flex h-12 items-center justify-center rounded-2xl bg-emerald-600 px-7 text-sm font-bold text-white shadow-lg shadow-emerald-600/20 transition hover:-translate-y-0.5 hover:bg-emerald-700 hover:shadow-emerald-600/30 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:translate-y-0 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none lg:min-w-[190px]"
                                            disabled={!canCreate}
                                        >
                                            {isSaving ? "Creating..." : "Create narration text"}
                                        </button>
                                    </form>

                                    {error && (
                                        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3" role="alert">
                                            <p className="text-sm font-medium text-red-700">{error}</p>
                                        </div>
                                    )}

                                    {sharedStatusMessage && !error && (
                                        <div className="mt-4 rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-3" role="status">
                                            <p className="text-sm font-semibold text-cyan-800">{sharedStatusMessage}</p>
                                        </div>
                                    )}
                                </section>

                                <section className="relative overflow-hidden rounded-[34px] border border-blue-100 bg-white shadow-[0_24px_70px_-46px_rgba(37,99,235,0.72)] ring-1 ring-blue-50">
                                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-sky-400 to-indigo-500" />
                                    <div className="border-b border-blue-100 bg-gradient-to-br from-white via-blue-50/60 to-indigo-50/30 p-5">
                                        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                                            <div>
                                                <div className="flex items-center gap-3">
                                                    <h2 className="text-xl font-bold tracking-tight text-slate-950">Saved narration texts</h2>
                                                    {!isLoading && presentations.length > 0 && (
                                                        <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700 ring-1 ring-blue-200">
                                                            {searchQuery ? `${visibleTexts.length}/${presentations.length}` : presentations.length}
                                                        </span>
                                                    )}
                                                    {sharedTextCount > 0 && (
                                                        <span className="rounded-full bg-cyan-50 px-2.5 py-1 text-xs font-bold text-cyan-700 ring-1 ring-cyan-200">
                                                            {sharedTextCount} shared
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="mt-1 text-sm leading-6 text-slate-500">
                                                    Open a text to add content, edit transcripts, or start narration.
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(220px,1fr)_180px] lg:w-[490px]">
                                                <label className="sr-only" htmlFor="narration-text-search">Search saved narration texts</label>
                                                <input
                                                    id="narration-text-search"
                                                    className="h-11 rounded-2xl border border-slate-200 bg-white px-4 text-sm text-slate-950 shadow-sm transition placeholder:text-slate-400 hover:border-blue-300 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
                                                    type="search"
                                                    placeholder="Search narration texts"
                                                    value={searchQuery}
                                                    onChange={(event) => setSearchQuery(event.target.value)}
                                                />

                                                <label className="sr-only" htmlFor="narration-text-sort">Sort saved texts</label>
                                                <select
                                                    id="narration-text-sort"
                                                    className="h-11 rounded-2xl border border-slate-200 bg-white px-4 text-sm text-slate-950 shadow-sm transition hover:border-slate-300 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
                                                    value={sortMode}
                                                    onChange={(event) => setSortMode(event.target.value)}
                                                >
                                                    {SORT_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {isLoading ? (
                                        <div className="flex min-h-[220px] items-center justify-center text-center">
                                            <p className="text-sm text-slate-500">Loading saved texts...</p>
                                        </div>
                                    ) : visibleTexts.length === 0 ? (
                                        <div className="flex min-h-[220px] flex-col items-center justify-center text-center">
                                            <p className="font-medium text-slate-950">
                                                {presentations.length === 0 ? "No saved narration texts yet" : "No texts match your search"}
                                            </p>
                                            <p className="mt-1 text-sm text-slate-500">
                                                {presentations.length === 0 ? "Create your first text above." : "Try another title or language."}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto bg-gradient-to-br from-white via-blue-50/15 to-white">
                                            <div className="min-w-[860px]">
                                                <div className="sticky top-0 z-10 grid grid-cols-[minmax(0,1fr)_140px_190px_120px_230px] gap-4 border-b border-blue-100 bg-blue-50/80 px-6 py-3 text-xs font-black uppercase tracking-wide text-blue-500 backdrop-blur">
                                                    <span>Text</span>
                                                    <span>Language</span>
                                                    <span>Status</span>
                                                    <span>Updated</span>
                                                    <span className="text-right">Actions</span>
                                                </div>

                                                {visibleTexts.map((presentation, index) => {
                                                    const key = `${presentation.title}-${presentation.language}`;
                                                    const sharedKey = getPresentationKey(presentation);
                                                    const sharedRecordings = sharedRecordingsByText[sharedKey] || [];
                                                    const isShared = sharedRecordings.length > 0;
                                                    const isUnsharing = unsharingKey === sharedKey;
                                                    const isDeleting = deletingKey === key;
                                                    const statusInfo = getTextStatus(presentation);

                                                    return (
                                                        <div
                                                            key={`${key}-${index}`}
                                                            className="grid grid-cols-[minmax(0,1fr)_140px_190px_120px_230px] gap-4 border-b border-slate-100 border-l-4 border-l-transparent px-6 py-4 transition last:border-b-0 hover:border-l-blue-400 hover:bg-blue-50/40"
                                                        >
                                                            <div className="min-w-0">
                                                                <p className="truncate font-bold text-slate-950">{presentation.title}</p>
                                                            </div>

                                                            <div className="text-sm font-medium text-slate-600">{presentation.language}</div>

                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${statusInfo.className}`}>
                                                                    {statusInfo.label}
                                                                </span>
                                                                {isShared && (
                                                                    <span className="inline-flex items-center rounded-full bg-cyan-50 px-2.5 py-1 text-xs font-black text-cyan-700 ring-1 ring-cyan-200">
                                                                        {sharedRecordings.length > 1 ? `Shared · ${sharedRecordings.length}` : "Shared"}
                                                                    </span>
                                                                )}
                                                                {isLoadingSharedRecordings && !isShared && (
                                                                    <span className="inline-flex items-center rounded-full bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-400 ring-1 ring-slate-200">
                                                                        Checking…
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <div className="text-sm font-medium text-slate-500">{formatTextDate(presentation)}</div>

                                                            <div className="flex items-center justify-end gap-2">
                                                                <button
                                                                    type="button"
                                                                    className="inline-flex h-10 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-600/20 transition hover:-translate-y-0.5 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                                                    onClick={() => setPresentation({ title: presentation.title, language: presentation.language })}
                                                                >
                                                                    Open
                                                                </button>
                                                                {isShared && (
                                                                    <button
                                                                        type="button"
                                                                        className="inline-flex h-10 items-center justify-center rounded-xl border border-cyan-200 bg-cyan-50 px-3 text-sm font-black text-cyan-800 transition hover:bg-cyan-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                                        onClick={() => unsharePresentationRecordings(presentation)}
                                                                        disabled={Boolean(unsharingKey)}
                                                                    >
                                                                        {isUnsharing ? "Unsharing..." : "Unshare"}
                                                                    </button>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    className="inline-flex h-10 items-center justify-center rounded-xl px-3 text-sm font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                                    onClick={() => requestDeletePresentation(presentation)}
                                                                    disabled={isDeleting || Boolean(unsharingKey)}
                                                                >
                                                                    {isDeleting ? "Deleting..." : "Delete"}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </section>
                            </div>
                        </main>

                        {textToDelete && (
                            <DeleteTextDialog
                                text={textToDelete}
                                onCancel={cancelDeletePresentation}
                                onConfirm={confirmDeletePresentation}
                                isDeleting={Boolean(deletingKey)}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default YourPresentations;